package fr.manyverse.app.infrastructure.audiopicker

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.AudioData
import fr.manyverse.app.infrastructure.filepicker.PickerContent
import fr.manyverse.app.infrastructure.filepicker.pickerInitialDirectory
import fr.manyverse.app.infrastructure.filepicker.rememberPickerDialogSettings
import io.github.vinceglb.filekit.PlatformFile
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.extension
import io.github.vinceglb.filekit.mimeType
import io.github.vinceglb.filekit.name
import io.github.vinceglb.filekit.readBytes
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.select_audio_file
import org.jetbrains.compose.resources.stringResource

private const val TAG = "AudioPicker"

private val AUDIO_EXTENSIONS = listOf("mp3", "wav", "ogg", "flac", "aac", "m4a")

/**
 * Composable function to create and remember an audio picker launcher (FileKit native dialog).
 * @param onResult Callback invoked when an audio file is picked (or null if canceled or unreadable)
 * @return A lambda to launch the audio picker
 */
@Composable
fun rememberAudioPickerLauncher(
    onResult: (AudioData?) -> Unit
): () -> Unit {
    val currentOnResult by rememberUpdatedState(onResult)
    val scope = rememberCoroutineScope()

    val launcher = rememberFilePickerLauncher(
        type = FileKitType.File(AUDIO_EXTENSIONS),
        directory = remember { pickerInitialDirectory(PickerContent.Audio) },
        dialogSettings = rememberPickerDialogSettings(PickerContent.Audio, stringResource(Res.string.select_audio_file)),
        onError = { error ->
            AppLogger.error(TAG, "Audio picker failed", error)
            currentOnResult(null)
        },
        onResult = { file ->
            if (file == null) {
                currentOnResult(null)
                return@rememberFilePickerLauncher
            }
            scope.launch {
                val audio = try {
                    AudioData(file.readBytes(), file.audioMimeType(), file.name)
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    AppLogger.error(TAG, "Audio file unreadable", e)
                    null
                }
                currentOnResult(audio)
            }
        }
    )

    return remember(launcher) { { launcher.launch() } }
}

/**
 * MIME type deduced from the extension first: platforms report different types for the same
 * format (e.g. iOS gives "audio/vnd.wave" for WAV), the extension keeps it within what
 * [fr.manyverse.app.domain.validation.AudioValidator] accepts.
 */
private fun PlatformFile.audioMimeType(): String = when (extension.lowercase()) {
    "mp3" -> "audio/mpeg"
    "wav" -> "audio/wav"
    "ogg" -> "audio/ogg"
    "flac" -> "audio/flac"
    "aac" -> "audio/aac"
    "m4a" -> "audio/mp4"
    else -> mimeType()?.let { "${it.primaryType}/${it.subtype}" } ?: "audio/mpeg"
}
